import { useQuery, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import { selectTokenId } from '@redux';
import moment from 'moment';

import { getOrgTreeBaseUrl } from '@legacy-modules/common/services/EnvironmentConfigurationService';
import { OrgUnitResult } from '@legacy-modules/metrics2/models/entities/organization/OrgUnitResult';
import { OrgUnitPart } from '@legacy-modules/metrics2/models/entities/organization/OrgUnit';

type InputParameters = {
  orgKeys: string[];
  parts: OrgUnitPart[];
  orgType?: string;
  from?: moment.Moment;
  to?: moment.Moment;
  enabled?: boolean;
};

export type MultiOrgTreeQueryResult = {
  units: OrgUnitResult[];
};

export default function useMultiOrgTree(parameters: InputParameters): UseQueryResult<Map<string, OrgUnitResult>> {
  const { orgKeys, parts, orgType, from, to, enabled } = parameters;
  const tokenId = useSelector(selectTokenId);

  const effectiveFrom = from?.format('YYYY-MM-DD');
  const effectiveTo = to?.format('YYYY-MM-DD');
  const url = new URL(`${getOrgTreeBaseUrl()}/orgtree/select`);
  const requestParams = {
    orgKeys,
    parts,
    ...(orgType && { orgType }),
    ...(effectiveFrom && { from: effectiveFrom }),
    ...(effectiveTo && { to: effectiveTo }),
  };

  return useQuery<MultiOrgTreeQueryResult, unknown, Map<string, OrgUnitResult>>({
    queryKey: ['multi-org-tree', orgKeys, parts, orgType, effectiveFrom, effectiveTo],
    enabled: enabled && orgKeys?.length > 0 && parts?.length > 0,
    queryFn: () =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: tokenId,
        },
        body: JSON.stringify(requestParams),
      }).then((res) => res.json()),
    select: (data) =>
      data.units?.reduce((acc, unit) => acc.set(unit.orgKey, unit), new Map<string, OrgUnitResult>()) ||
      new Map<string, OrgUnitResult>(),
    keepPreviousData: true,
  });
}
